import { ParseResult } from "papaparse";
import React, { ChangeEvent, useState, useEffect } from "react";
import IVendorUploadDataRow from "../../../../models/IVendorUploadDataRow";
import IOwRadioSelectOption from "../../../../models/IOwRadioSelectOption";
import ICreateVendorCsvDTO from "../../../../models/DTO/ICreateVendorCsvDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import OwRadioSelect from "../../../shared/OwRadioSelect/OwRadioSelect";
import OwCsvReader from "../../../shared/OwCsvReader/OwCsvReader";
import OwButton from "../../../shared/OwButton/OwButton";
import OwS3Uploader from "../../../shared/OwS3Uploader/OwS3Uploader";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import IOnCreateVendorCsvDTO from "../../../../models/DTO/IOnCreateVendorCsvDTO";
import FileUploadResourceTypeEnum from "../../../../models/FileUploadResourceTypeEnum";
import S3UploadTypeEnum from "../../../../models/S3UploadTypeEnum";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import { useAppContextNonNull } from "@contexts/AppContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { CustomImportUploader } from "@components/shared/CustomImportUploader/CustomImportUploader";
import CustomImportTypeEnum from "@models/CustomImportTypeEnum";
// TODO: Import CustomImportUploader component when available
// import CustomImportUploader from "../../../shared/CustomImportUploader/CustomImportUploader";
// import CustomImportTypeEnum from "../../../../models/CustomImportTypeEnum";

type Props = {
    closeModalFn: () => void;
    totalVendors: number;
};

const VendorCsvModalUpload: React.FC<Props> = ({ closeModalFn, totalVendors }) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();

    const [file, setFile] = useState<any>(null);
    const [csvData, setCsvData] = useState<any>(null);
    const [isProcessing] = useState(false);
    const [uploadActive, setUploadActive] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [selectedUploadType, setSelectedUploadType] = useState<string>(() => {
        // Set default: Custom Import if available, otherwise Bulk Uploader
        if (supplierModel.has_custom_imports) {
            return "custom_import";
        }
        return "bulk_uploader";
    });
    const [totalRecords, setTotalRecords] = useState(0);
    const [processedRows, setProcessedRows] = useState(0);

    const onCreateVendorCsv = (data: IOnCreateVendorCsvDTO) => {
        setTotalRecords(data.totalRows);
        setProcessedRows(data.processedRows);
        setShowResults(true);
        setUploadActive(false);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.VendorsOnCreateVendorCsv, fn: onCreateVendorCsv }];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    const csvLoaded = (csvData?: ParseResult<any>, file?: any) => {
        if (csvData) {
            setCsvData(csvData);
            if (file) {
                setFile(file);
            }
        }
    };

    const setupInventoryCSVData = (csvData: ParseResult<any>) => {
        const vendors: IVendorUploadDataRow[] = [];

        // console.log('csvData', csvData);

        for (const row of csvData.data) {
            const data: Partial<IVendorUploadDataRow> = {
                name: row["Name"],
                email: row["Email"],
                customerInternalId: row["Customer Internal Id"],
                repGroup: row["Rep Group"],
                shipToAddressInternalId: row["Ship To Address Internal Id"],
                shipToCatalog: row["Ship To Catalog"],
                shipToPriceGroup: row["Ship To Price Group"],
                shipToDefault: row["Ship To Default"],
                shipToAddrLineOne: row["Ship To Address Line One"],
                shipToAddrLineTwo: row["Ship To Address Line Two"],
                shipToAddrLineThree: row["Ship To Address Line Three"],
                shipToCity: row["Ship To City"],
                shipToState: row["Ship To State"],
                shipToPostalCode: row["Ship To Postal Code"],
                shipToCountry: row["Ship To Country"],
                shipToPhone: row["Ship To Phone"],
                shipToFax: row["Ship To Fax"],
                billToAddressInternalId: row["Bill To Address Internal Id"],
                billToAddrLineOne: row["Bill To Address Line One"],
                billToAddrLineTwo: row["Bill To Address Line Two"],
                billToAddrLineThree: row["Bill To Address Line Three"],
                billToCity: row["Bill To City"],
                billToState: row["Bill To State"],
                billToPostalCode: row["Bill To Postal Code"],
                billToCountry: row["Bill To Country"],
                billToPhone: row["Bill To Phone"],
                billToFax: row["Bill To Fax"],
                category: row["Category"],
                catalog: row["Catalog"] || row["Catalog Lock"],
                priceGroup: row["Price Group"] || row["Price Group Lock"],
                guid: row["Vendor Guid (Do Not Edit)"],
                shipToGuid: row["Vendor Ship To Guid (Do Not Edit)"],
            };

            if (row["Ship To Name"]) {
                data.shipToName = row["Ship To Name"];
            }

            if (row["Price Adjustment"]) {
                data.priceAdjustment = row["Price Adjustment"];
            }

            // @ts-expect-error
            vendors.push(data);
        }

        const payload: ICreateVendorCsvDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            rows: JSON.stringify(vendors),
        };

        socket.emit(EndpointsEnum.VendorsCreateVendorCsv, payload);

        setTotalRecords(0);
        setProcessedRows(0);
        setShowResults(false);
    };

    const doUpload = () => {
        setUploadActive(true);
        if (csvData) {
            setupInventoryCSVData(csvData);
        }
    };

    const closeModal = () => {
        closeModalFn();
    };

    const handleUploadTypeChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        setSelectedUploadType(value);
    };

    const uploadOptions: IOwRadioSelectOption[] = [
        {
            label: "Basic Uploader",
            value: "basic_uploader",
        },
        {
            label: "Bulk Uploader (More than 100 records)",
            value: "bulk_uploader",
        },
    ];

    if (supplierModel.has_custom_imports) {
        uploadOptions.push({
            label: "Custom Import",
            value: "custom_import",
        });
    }

    return (
        <div className="VendorCsvModalUpload p-2">
            <div className={""}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-cloud-upload-alt"} title={"upload_csv"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div className={""}>
                <div>
                    {uploadOptions.length > 1 ? (
                        <div className={"mt-3 mb-3"}>
                            <OwRadioSelect
                                options={uploadOptions}
                                name={"uploadTypeChange"}
                                fn={handleUploadTypeChange}
                                defaultValue={selectedUploadType}
                            />
                        </div>
                    ) : null}
                </div>

                <div className={""}>
                    {selectedUploadType === "bulk_uploader" ? (
                        <OwS3Uploader
                            resourceType={FileUploadResourceTypeEnum.vendorCsv}
                            socket={socket}
                            isReadOnly={false}
                            userModel={userModel}
                            supplierModel={supplierModel}
                            isS3Upload={true}
                            s3UploadType={S3UploadTypeEnum.VENDOR_CSV}
                        />
                    ) : selectedUploadType === "custom_import" ? (
                        <div className="mt-3">
                            <CustomImportUploader type={CustomImportTypeEnum.ACCOUNTS} />
                        </div>
                    ) : (
                        <div>
                            <div className={"drop-area"}>
                                <OwCsvReader onFileLoaded={csvLoaded} isProcessing={isProcessing} />
                            </div>

                            <div className={""}>
                                {file ? (
                                    <div>
                                        <OwLabel title={"loaded_file"} noBottomMargin={true} isBold={true} isUnderline={true} />:{" "}
                                        {file.name}
                                    </div>
                                ) : null}
                            </div>

                            <div className={""}>
                                <div className={"mt-4"}>
                                    <OwButton title={"upload"} fn={doUpload} active={uploadActive} disableOnActive={true} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showResults ? (
                <div className={"mt-2"}>
                    <div>
                        <OwLabel title={"total_records"} noBottomMargin={true} className={"me-3"} isBold={true} />
                        {totalRecords}
                    </div>

                    <div>
                        <OwLabel title={"records_updated"} noBottomMargin={true} className={"me-3"} isBold={true} />
                        {processedRows}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default VendorCsvModalUpload;
